/**
 * Récepteur : écrit les morceaux reçus et vérifie l'intégrité.
 *
 * ## Trois invariants vérifiés, pas seulement supposés
 *
 * 1. **Séquence stricte.** Le canal étant fiable et ordonné, tout écart de séquence
 *    trahit un rejeu ou une anomalie. On interrompt plutôt que d'écrire un fichier faux.
 *    C'est `TransferChannel` qui l'impose, en amont.
 * 2. **Un seul fichier à la fois.** Les morceaux d'un fichier arrivent groupés ; recevoir
 *    un morceau du fichier 3 pendant qu'on écrit le fichier 2 signale un défaut réel.
 * 3. **Empreinte et taille.** Après le dernier morceau, l'émetteur annonce le SHA-256 et
 *    une taille. Les deux sont comparés : une coupure réseau qui aurait tronqué le flux
 *    est détectée avant que l'utilisateur n'ouvre un fichier corrompu.
 *
 * Le troisième point est ce qui distingue un transfert « qui a l'air d'avoir marché »
 * d'un transfert dont on sait qu'il a marché.
 */

import {
  CHUNK_SIZE,
  formatBytes,
  type Bytes,
  type DcControl,
  type FileMeta,
} from '@attrape/shared';
import { sha256 } from '@noble/hashes/sha2.js';
import { LocalizedError, msg, type MessageDescriptor } from '../../i18n/index.js';
import type { TransferChannel } from './channel.js';
import {
  chunkCountFor,
  createBitmap,
  deleteSession,
  markChunkReceived,
  missingCount,
  missingRanges,
  saveSession,
  type ResumeSession,
} from './resume-store.js';
import { toHex } from './sender.js';
import type { FileSink } from './writer.js';

export interface ReceiverProgress {
  fileIndex: number;
  fileCount: number;
  fileName: string;
  fileBytes: number;
  fileTotal: number;
  totalReceived: number;
  totalBytes: number;
  bytesPerSecond: number;
  etaSeconds: number | null;
}

export interface ReceiverFileResult {
  index: number;
  name: string;
  bytes: number;
  /** `true` si l'empreinte et la taille annoncées correspondent à ce qui a été reçu. */
  verified: boolean;
}

export interface ReceiverResult {
  files: ReceiverFileResult[];
  totalBytes: number;
  /** `true` si **tous** les fichiers ont été vérifiés avec succès. */
  allVerified: boolean;
}

export interface RunReceiverOptions {
  channel: TransferChannel;
  files: FileMeta[];
  sinks: FileSink[];
  onProgress: (progress: ReceiverProgress) => void;
  signal: AbortSignal;
  /**
   * Contexte de reprise, quand une session précédente existe pour ce lot.
   *
   * Absent au premier transfert : le récepteur part alors d'un bitmap vide. Présent
   * après une coupure : le bitmap est réutilisé pour ne pas redemander ce qui est déjà
   * sur le disque, et pour marquer les morceaux au fil de l'eau.
   */
  resume?: ReceiverResumeContext;
}

export interface ReceiverResumeContext {
  roomId: string;
  /** Bitmap par index de fichier. Créé à la volée si absent. */
  bitmaps: Map<number, Uint8Array>;
  /** Poignée de fichier persistable, quand la stratégie d'écriture la fournit. */
  handle?: FileSystemFileHandle | null;
  /**
   * Appelé dès qu'une session de reprise devient persistable.
   *
   * C'est ce qui permet à `session.ts` de conserver l'état **avant** la fin du transfert :
   * une coupure ne prévient pas, on ne peut donc pas persister seulement à la fin.
   */
  onSessionCreated?: (session: ResumeSession) => void;
}

interface Deferred<T> {
  promise: Promise<T>;
  resolve: (value: T) => void;
  reject: (reason: unknown) => void;
}

function createDeferred<T>(): Deferred<T> {
  let resolve!: (value: T) => void;
  let reject!: (reason: unknown) => void;
  const promise = new Promise<T>((res, rej) => {
    resolve = res;
    reject = rej;
  });
  return { promise, resolve, reject };
}

export async function runReceiver(options: RunReceiverOptions): Promise<ReceiverResult> {
  const { channel, files, sinks, onProgress, signal, resume } = options;

  const totalBytes = files.reduce((sum, file) => sum + file.size, 0);
  const startedAt = performance.now();

  let currentFile = 0;
  let fileBytes = 0;
  let totalReceived = 0;
  let hasher = sha256.create();
  let lastReport = 0;

  const results: ReceiverFileResult[] = [];
  const completion = createDeferred<void>();

  /**
   * Index du prochain morceau attendu **dans le fichier courant**.
   *
   * Sur une reprise, les morceaux n'arrivent pas dans l'ordre 0, 1, 2… : l'émetteur ne
   * renvoie que les plages manquantes, dans l'ordre où elles ont été demandées. On
   * parcourt donc la même liste de plages côté récepteur, ce qui garantit que les deux
   * côtés désignent le même morceau à chaque trame.
   *
   * Se fier au seul compteur incrémental serait faux après une reprise : le premier
   * morceau reçu ne serait pas le morceau 0, et le bitmap serait marqué au mauvais
   * endroit — le fichier serait alors déclaré incomplet à la fin, alors qu'il est
   * complet, ou pire, accepté avec des trous.
   */
  let chunkIndex = 0;

  /**
   * File des morceaux attendus pour le fichier courant, dans l'ordre d'arrivée.
   *
   * Sans reprise, elle est simplement `[0, 1, 2, …]`. Avec reprise, elle est construite
   * à partir des plages manquantes mémorisées, qui sont exactement celles demandées à
   * l'émetteur.
   */
  const expectedChunks = (fileIndex: number): number[] => {
    const meta = files[fileIndex];
    if (!meta) return [];

    const total = chunkCountFor(meta.size);
    const stored = resume?.bitmaps.get(fileIndex);
    if (!stored) {
      return Array.from({ length: total }, (_, index) => index);
    }

    // Les morceaux attendus sont ceux qui manquent : c'est exactement ce que l'émetteur
    // a été prié de renvoyer.
    return missingRanges(stored, total).flatMap(([start, end]) =>
      Array.from({ length: end - start + 1 }, (_, offset) => start + offset),
    );
  };

  let expected = expectedChunks(0);
  let expectedPosition = 0;
  // Le premier morceau attendu n'est pas forcément le 0 : sur une reprise, les morceaux
  // déjà présents ne seront pas renvoyés.
  chunkIndex = expected[0] ?? 0;

  /** Bitmap du fichier courant. Rechargé à chaque changement de fichier. */
  let bitmap = resume?.bitmaps.get(0) ?? createBitmap(files[0]?.size ?? 0);

  /**
   * Compteur de morceaux écrits depuis la dernière persistance.
   *
   * Persister à chaque morceau ferait une écriture IndexedDB toutes les 63 KiB, ce qui
   * pèserait sur le débit. Persister toutes les quelques centaines de morceaux borne la
   * perte en cas de coupure à quelques mégaoctets — largement préférable au coût d'une
   * écriture par morceau.
   */
  let sincePersist = 0;
  const PERSIST_EVERY_CHUNKS = 64;

  const persist = (force: boolean): void => {
    if (!resume) return;
    if (!force && sincePersist < PERSIST_EVERY_CHUNKS) return;
    sincePersist = 0;

    const meta = files[currentFile];
    if (!meta) return;

    resume.bitmaps.set(currentFile, bitmap);
    const session: ResumeSession = {
      fileId: meta.id,
      roomId: resume.roomId,
      name: meta.name,
      size: meta.size,
      lastModified: meta.lastModified,
      received: bitmap,
      firstMissing: 0,
      handle: resume.handle ?? null,
      updatedAt: Date.now(),
    };
    resume.onSessionCreated?.(session);
    // L'écriture est volontairement non attendue : bloquer la boucle de réception sur
    // IndexedDB ralentirait le transfert pour un gain nul. Une perte de quelques morceaux
    // au pire se rattrape à la reprise suivante.
    void saveSession(session);
  };

  const currentMeta = (): FileMeta | undefined => files[currentFile];
  const currentSink = (): FileSink | undefined => sinks[currentFile];

  const report = (force: boolean): void => {
    const now = performance.now();
    if (!force && now - lastReport < 120) return;
    lastReport = now;

    const meta = currentMeta();
    if (!meta) return;

    const elapsedSeconds = Math.max(0.001, (now - startedAt) / 1000);
    const bytesPerSecond = totalReceived / elapsedSeconds;
    const remaining = totalBytes - totalReceived;

    onProgress({
      fileIndex: currentFile,
      fileCount: files.length,
      fileName: meta.name,
      fileBytes,
      fileTotal: meta.size,
      totalReceived,
      totalBytes,
      bytesPerSecond,
      etaSeconds: bytesPerSecond > 0 ? remaining / bytesPerSecond : null,
    });
  };

  const fail = (error: unknown): void => {
    completion.reject(error);
  };

  /**
   * Signale une anomalie protocolaire.
   *
   * Le détail (« morceau 3 alors que le fichier 2 est en cours ») part au journal de
   * développement, où il est exploitable. L'écran affiche une phrase qui dit quoi faire :
   * afficher le détail brut n'aiderait pas l'utilisateur, le taire priverait le développeur.
   */
  const failTechnical = (detail: string, descriptor?: MessageDescriptor): void => {
    if (import.meta.env.DEV) console.error('[réception] anomalie protocolaire :', detail);
    fail(new LocalizedError(descriptor ?? msg.key('errors.integrityFailed')));
  };

  const handleChunk = async (fileIndex: number, data: Bytes): Promise<void> => {
    if (signal.aborted) return;

    if (fileIndex !== currentFile) {
      failTechnical(
        `Morceau reçu pour le fichier ${fileIndex} alors que le fichier ${currentFile} ` +
          'est en cours de réception.',
      );
      return;
    }

    const meta = currentMeta();
    const sink = currentSink();
    if (!meta || !sink) {
      failTechnical(`Morceau reçu pour un fichier non annoncé (index ${fileIndex}).`);
      return;
    }

    // Garde-fou : refuser d'écrire au-delà de la taille annoncée évite qu'un pair
    // malveillant ne remplisse le disque en annonçant un petit fichier.
    if (fileBytes + data.byteLength > meta.size) {
      failTechnical(
        `Le fichier « ${meta.name} » dépasse la taille annoncée (${meta.size} octets).`,
      );
      return;
    }

    // Garde-fou de reprise : si la file des morceaux attendus est épuisée, l'émetteur
    // envoie plus de morceaux que demandé. C'est le cas d'une reprise ignorée par
    // l'émetteur (fichier source modifié entre-temps, donc refusé et renvoyé en
    // entier) : continuer écrirait un flux complet dans une destination déjà
    // partiellement remplie — un fichier complet mais corrompu. On interrompt plutôt
    // que de produire silencieusement un fichier faux.
    if (expectedPosition >= expected.length) {
      failTechnical(
        `Le fichier « ${meta.name} » : morceau en surplus alors que tous les morceaux ` +
          'attendus ont déjà été reçus (reprise non honorée par l’émetteur ?).',
      );
      return;
    }

    hasher.update(data);
    // Sur une reprise, le morceau n'est pas contigu au curseur du sink : on écrit à sa
    // position absolue. Sans cela, un sink en mode ajout (File System Access, mémoire)
    // écrirait les morceaux renvoyés bout à bout à la fin du fichier — reconstructeur
    // d'un fichier complet mais corrompu.
    await sink.write(data, resume ? chunkIndex * CHUNK_SIZE : undefined);

    // Marquer le morceau reçu : c'est ce qui rend la reprise exacte plutôt
    // qu'approximative. L'index vient de la file des morceaux attendus, et non d'un
    // compteur incrémental — après une reprise, le premier morceau reçu n'est pas le
    // morceau 0.
    if (resume) {
      markChunkReceived(bitmap, chunkIndex);
      sincePersist += 1;
    }
    expectedPosition += 1;
    chunkIndex = expected[expectedPosition] ?? 0;

    fileBytes += data.byteLength;
    totalReceived += data.byteLength;
    report(false);
    persist(false);
  };

  const handleControl = async (control: DcControl): Promise<void> => {
    switch (control.t) {
      case 'file-done': {
        if (control.fileIndex !== currentFile) {
          failTechnical('Confirmation de fin pour un fichier inattendu.');
          return;
        }

        const meta = currentMeta();
        const sink = currentSink();
        if (!meta || !sink) {
          failTechnical('Confirmation de fin sans fichier en cours.');
          return;
        }

        const actualHash = toHex(hasher.digest());

        // Toutes les parties du fichier sont-elles présentes ? C'est la vérification
        // structurelle, et la seule qui ait du sens après une reprise : l'empreinte
        // reçue couvre le fichier **entier**, alors que le récepteur n'en a reçu qu'une
        // partie dans cette session.
        const totalChunks = chunkCountFor(meta.size);
        // Capturé maintenant : le compte rendu d'échec plus bas interroge les octets reçus,
        // or la remise à zéro pour le fichier suivant (`fileBytes = 0`) passe avant lui.
        // Sans cette copie, un message « 0 sur 120 Mo » accuserait le réseau à tort.
        const receivedBytes = fileBytes;
        const structural = resume
          ? missingCount(missingRanges(bitmap, totalChunks)) === 0
          : receivedBytes === meta.size;

        // L'empreinte n'est comparée que lorsque le récepteur a vu **tout** le fichier
        // dans cette session. Après une reprise, comparer une empreinte partielle à une
        // empreinte complète donnerait invariablement « différent », et ferait rejeter un
        // fichier correct — un faux négatif déterministe, donc pire qu'aucun contrôle.
        //
        // Ce qui protège la reprise est ailleurs : la vérification structurelle ci-dessus
        // (aucun morceau manquant) et l'identité du fichier source (nom, taille, date),
        // contrôlée à l'ouverture de la session. L'ADR-002 documente cette limite.
        const hashComparable = !resume;
        const hashMatches = hashComparable && actualHash === control.sha256;
        const verified = structural && (!hashComparable || hashMatches);

        // Un fichier non vérifié n'est pas remis à l'utilisateur : mieux vaut un échec
        // explicite qu'un fichier corrompu qui semble valide.
        if (verified) {
          await sink.commit();
        } else {
          await sink.abort();
        }

        // L'état de reprise a rempli son rôle : il ne doit pas survivre à la fin du
        // transfert, **quel que soit le résultat**.
        //
        // Ce fichier est réglé — soit écrit et vérifié, soit abandonné. Le conserver
        // n'apporte rien et nuit gravement : `fileId` étant stable pour un même fichier
        // (`index-nom-taille-date`), une tentative ultérieure sur le même fichier
        // rechargerait ce bitmap. Or les morceaux attendus sont exactement les morceaux
        // **manquants** : avec un bitmap plein, la file est vide, et le premier morceau
        // reçu est refusé comme « en surplus ». L'utilisateur voit alors
        // « les données reçues ne sont pas conformes » — un faux négatif qui l'accuse
        // d'un échec réseau inexistant, et qui se reproduit à chaque essai.
        //
        // À l'échec, la purge se justifie autant : le transfert est abandonné, et
        // reprendre sur cette base est précisément ce que l'utilisateur ne veut pas.
        void deleteSession(meta.id);

        results.push({
          index: currentFile,
          name: meta.name,
          // Taille **du fichier**, pas des octets reçus dans cette session : sur une
          // reprise, rapporter les seuls octets de la session afficherait un fichier
          // tronqué à l'utilisateur alors qu'il est complet.
          bytes: meta.size,
          verified,
        });

        currentFile += 1;
        fileBytes = 0;
        // Nouveau fichier : le bitmap et l'index de morceau repartent de zéro. Sans cette
        // remise à zéro, les morceaux du second fichier seraient marqués dans le bitmap
        // du premier et la reprise deviendrait fausse.
        chunkIndex = 0;
        bitmap = resume?.bitmaps.get(currentFile) ?? createBitmap(files[currentFile]?.size ?? 0);
        expected = expectedChunks(currentFile);
        expectedPosition = 0;
        chunkIndex = expected[0] ?? 0;
        persist(true);
        // Nouvel objet de hachage : le précédent a été consommé par `digest()`.
        hasher = sha256.create();

        if (!verified) {
          // Le message affiché doit dire ce qui s'est réellement passé : un flux
          // interrompu (le cas courant sur un réseau qui lâche) n'est pas un fichier
          // corrompu, et l'utilisateur n'a pas la même chose à en faire. On distingue
          // donc les trois cas, et on chiffre l'interruption quand les nombres sont
          // exacts — c'est-à-dire hors reprise, où `fileBytes` ne compte que la
          // session courante.
          if (structural) {
            failTechnical(
              `« ${meta.name} » non vérifié (empreinte SHA-256 différente).`,
              msg.key('errors.checksumMismatch', { name: meta.name }),
            );
          } else if (resume) {
            failTechnical(
              `« ${meta.name} » non vérifié (morceaux manquants après reprise).`,
              msg.key('errors.incompleteReceiveResume', { name: meta.name }),
            );
          } else {
            const percent = meta.size > 0 ? (receivedBytes / meta.size) * 100 : 0;
            failTechnical(
              `« ${meta.name} » non vérifié (${receivedBytes}/${meta.size} octets reçus).`,
              msg.key('errors.incompleteReceive', {
                name: meta.name,
                received: formatBytes(receivedBytes),
                total: formatBytes(meta.size),
                percent: percent < 1 ? '< 1' : String(Math.round(percent)),
              }),
            );
          }
          return;
        }

        report(true);
        return;
      }

      case 'all-done': {
        if (currentFile !== files.length) {
          fail(
            new Error(
              `Transfert déclaré terminé, mais ${files.length - currentFile} fichier(s) ` +
                'n’ont pas été reçus.',
            ),
          );
          return;
        }
        completion.resolve();
        return;
      }

      case 'cancel': {
        fail(new LocalizedError(msg.key('errors.cancelledBySender')));
        return;
      }

      case 'error': {
        fail(new Error(control.message));
        return;
      }

      // `meta`, `accept`, `reject` sont traités par la session, en amont du transfert.
      // `resume-request` et `resume-accepted` le sont aussi : la reprise se négocie avant
      // le premier morceau, pas pendant la réception.
      case 'meta':
      case 'accept':
      case 'reject':
      case 'resume-request':
      case 'resume-accepted':
        return;

      default: {
        const exhaustive: never = control;
        failTechnical(`Message de contrôle inattendu : ${JSON.stringify(exhaustive)}`);
      }
    }
  };

  const unsubscribeChunk = channel.onChunk((fileIndex, data) => handleChunk(fileIndex, data));
  const unsubscribeControl = channel.onControl((control) => handleControl(control));

  /**
   * Le canal s'est fermé avant la fin annoncée : l'émetteur a disparu.
   *
   * Sans cette surveillance, le récepteur attendrait indéfiniment un `file-done` qui
   * n'arrivera jamais, et l'utilisateur verrait une barre figée sans explication. C'est
   * précisément le comportement que le PLAN §1.3 reproche aux services existants.
   *
   * Le message est volontairement distinct d'un échec réseau : la session peut être
   * reprise, et c'est ce que l'utilisateur doit comprendre.
   */
  const unsubscribeClose = channel.onClose(() => {
    if (signal.aborted) return;
    // `errors.transferInterrupted` dit déjà l'essentiel : le transfert s'est interrompu
    // de façon inattendue et il faut relancer. Un message plus technique n'apporterait
    // rien à l'utilisateur.
    fail(new LocalizedError(msg.key('errors.transferInterrupted')));
  });

  const onAbort = (): void => fail(new DOMException('Transfert annulé', 'AbortError'));
  signal.addEventListener('abort', onAbort, { once: true });

  try {
    await completion.promise;
  } finally {
    unsubscribeChunk();
    unsubscribeControl();
    unsubscribeClose();
    signal.removeEventListener('abort', onAbort);
  }

  return {
    files: results,
    totalBytes,
    allVerified: results.length === files.length && results.every((entry) => entry.verified),
  };
}
